import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Box, Button, LinearProgress, useMediaQuery } from '@mui/material'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import _, { filter, includes, throttle } from 'lodash'
import { UIEventHandler, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ArrowUpIcon from '../../../assets/icons/ArrowUp.svg?react'
import ShareIcon from '../../../assets/icons/Share.svg?react'
import { GuestMode } from '../../../components/GuestMode'
import { ProjectItem } from '../../../components/ProjectItem'
import { TextSelectionMenu } from '../../../components/TextSelectionMenu'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import { ShareDialog } from '../../../dialogs/ShareDialog/ShareDialog'
import { useDialogStore } from '../../../store/storeDialog'
import { useProfileStore } from '../../../store/storeProfile'
import { useRequestsStore } from '../../../store/storeRequests'
import { useWrapperStore } from '../../../store/storeWrapper'
import { theme } from '../../../theme'
import { RequestItemMenu } from '../RequestsPage/components/RequestItemMenu'
import { ScrollDownButton } from './components/ScrollDownButton'

import InfiniteScroll from 'react-infinite-scroll-component'
import { ReplyDTO } from '../../../api/gen/base'
import { useInfiniteReplyListQuery } from '../../../api/hooks/useInfiniteReplyListQuery'
import { WrapperRequestInput } from '../../../components/RequestInput/WrapperRequestInput'
import { useRealTimeReplyUpdates } from '../../../hooks/useRealTimeReplyUpdates'
import { Message } from './components/Message'
import { RegularRequestsChip } from './components/RegularRequestsChip'

export const RequestMessagesPage = () => {
    const params = useParams()
    const [searchParams] = useSearchParams()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isMobileSm = useMediaQuery(theme.breakpoints.down('sm'))

    const messagesEndRef = useRef<HTMLSpanElement | null>(null)
    const navigate = useNavigate()
    const [showScrollButton, setShowScrollButton] = useState(false)
    const [openShareDialog, setOpenShareDialog] = useState<boolean>(false)
    const [suggestionText, setSuggestionText] = useState('')
    const lastScroll = useRef(0)
    const scrollUpDistance = useRef(0)
    const listScrollTop = useRef(0)
    const messagesListRef = useRef<HTMLDivElement | null>(null)
    const [isInputMounted, setIsInputMounted] = useState(false)
    const [isModelOrAssistantsUnavailable, setIsModelOrAssistantsUnavailable] = useState(undefined)

    const {
        requestDetail,
        fetchRequestDetail,
        requestMessages,
        errorRequestDetail,
        setRequestMessages,
        isLoadingRequestDetail,
        isCreateRequestMessage,
    } = useRequestsStore((state) => state)

    const {
        setCurrentAgentAndAssistantIdDialog,
        isTempUser,
        setOpenSidebarType,
        setHideHeader,
        hideHeader,
        assistantSubscriptions,
        fetchAssistantSubscriptions,
    } = useWrapperStore((state) => state)

    const { modelDetail } = useWrapperStore((state) => state)
    const { userTariff } = useProfileStore((state) => state)
    const { updateThread } = useDialogStore()

    const threadId = +params.requestId

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        isFetchedAfterMount,
        refetch,
    } = useInfiniteReplyListQuery({
        thread_id: threadId,
    })

    const handleScrollToLastMessage = () => {
        const lastMessage = document.getElementById(`message-${requestMessages?.[0]?.id}`)

        if (lastMessage && messagesListRef.current) {
            const { scrollTop, offsetHeight } = messagesListRef.current

            const shouldScrollSmooth = Math.abs(scrollTop) > offsetHeight

            lastMessage.scrollIntoView(
                shouldScrollSmooth ? { behavior: 'smooth', block: 'start' } : undefined,
            )
        }
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const handleScrollToListEnd = () => {
        if (Math.abs(listScrollTop.current) <= 5) {
            scrollToBottom()
        }
    }

    useRealTimeReplyUpdates({
        threadId,
        scrollToStartOfLastMessage: () => {
            handleScrollToLastMessage()
        },
        scrollToListEnd: () => {
            // handleScrollToListEnd()
        },
    })

    useEffect(() => {
        fetchRequestDetail({ threadId: threadId })

        return () => {
            setOpenSidebarType(null)
            setShowScrollButton(false)
            setIsInputMounted(false)
        }
    }, [threadId])

    useEffect(() => {
        fetchAssistantSubscriptions()
    }, [])

    useEffect(() => {
        if (requestDetail && userTariff) {
            setIsModelOrAssistantsUnavailable(
                !isTempUser &&
                    (requestDetail.assistant_id
                        ? includes(
                              userTariff?.tariff?.restrictions?.available_assistants,
                              +requestDetail?.assistant_id,
                          )
                            ? false
                            : filter(assistantSubscriptions, {
                                    assistant_id: +requestDetail.assistant_id,
                                }).length === 0
                              ? true
                              : false
                        : !includes(
                              userTariff?.tariff?.restrictions?.available_models,
                              requestDetail?.agent,
                          )),
            )
        }
    }, [userTariff, requestDetail, assistantSubscriptions])

    useEffect(() => {
        refetch()
    }, [searchParams.get('is_archived')])

    useLayoutEffect(() => {
        if (data) {
            setRequestMessages(
                data.pages.reduce<ReplyDTO[]>((acc, page) => acc.concat(page.objects), []) ?? [],
                data.pages[0].total_count,
            )
        }
    }, [data])

    useEffect(() => {
        if (requestDetail && !isLoadingRequestDetail) {
            setCurrentAgentAndAssistantIdDialog({
                agent: requestDetail.agent,
                assistantId: requestDetail.assistant_id,
            })
        }
    }, [requestDetail, isLoadingRequestDetail])

    useEffect(() => {
        if (isCreateRequestMessage) {
            setSuggestionText('')
        }
    }, [isCreateRequestMessage])

    useLayoutEffect(() => {
        setHideHeader(isMobileSm)

        return () => {
            setHideHeader(false)
        }
    }, [isMobileSm])

    useEffect(() => {
        return () => {
            setSuggestionText('')
        }
    }, [requestDetail?.id])

    const handleStoreScrollPosition: UIEventHandler<HTMLDivElement> = (evt) => {
        const { scrollTop } = evt.currentTarget

        listScrollTop.current = scrollTop
    }

    const handleScroll = useCallback(
        throttle((evt) => {
            if (!evt.target) {
                return
            }

            const { scrollTop, scrollHeight, clientHeight } = evt.target

            if (isMobileSm) {
                const diff = scrollUpDistance.current - lastScroll.current

                if (scrollTop > lastScroll.current) {
                    scrollUpDistance.current = scrollTop
                }

                if (
                    diff >= clientHeight ||
                    Math.abs(scrollTop) > scrollHeight - clientHeight - 150
                ) {
                    setHideHeader(false)
                } else {
                    setHideHeader(true)
                }

                lastScroll.current = scrollTop
            }

            const absScrollTop = Math.abs(scrollTop)
            const offset = 2000
            const displayScrollButton = absScrollTop > offset

            setShowScrollButton((prev) =>
                displayScrollButton !== prev ? displayScrollButton : prev,
            )
        }, 300),
        [lastScroll, scrollUpDistance, isMobileSm],
    )

    const handleClickOpenShareDialog = () => {
        setOpenShareDialog(true)
    }

    const handleClickCloseShareDialog = () => {
        setOpenShareDialog(false)
    }

    const handleCopy = () => {
        if (requestDetail.shared) {
            return
        }

        updateThread({ threadId: requestDetail.id }, { shared: true })
    }

    const handleOnChangeSuggestionText = (text: string) => setSuggestionText(text)

    const isModelRestricted =
        !requestDetail?.is_archived && modelDetail && modelDetail.is_restricted === true

    const shouldShowMessagesPlaceholder =
        (isFetching && !isFetchedAfterMount && !isFetchingNextPage) ||
        (!isInputMounted && !isModelRestricted)

    useLayoutEffect(() => {
        let timerId: NodeJS.Timeout
        // скролл к последнему сообщению после начальной загрузки сообщений
        if (!shouldShowMessagesPlaceholder) {
            handleScrollToLastMessage()

            // так как на мобилках скрывается хедер, нужно вызвать после скрытия
            timerId = setTimeout(() => {
                handleScrollToLastMessage()
            }, 200)
        }

        return () => {
            clearTimeout(timerId)
        }
    }, [shouldShowMessagesPlaceholder])

    return (
        <>
            <BlockWrapper
                sx={{
                    position: 'relative',
                    border: isTempUser ? '4px solid #006CFD' : 'none',
                    boxSizing: 'border-box',
                }}>
                <GuestMode />
                <Grid
                    sx={{
                        height: `calc(100dvh - ${(isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT) - (hideHeader ? 44 : 0)}px)`,
                        pt: 2,
                        px: 2,
                    }}>
                    <Grid
                        container
                        sx={{
                            height: `100%`,
                            position: 'relative',
                            left: 0,
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            alignContent: 'flex-start',
                            textAlign: 'left',
                            gap: 'unset',
                            flexWrap: 'nowrap',
                            justifyContent: 'space-between',
                        }}
                        spacing={4}>
                        <Stack direction="column" sx={{ height: '56px', width: '100%' }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    height: '56px',
                                }}>
                                <Stack
                                    direction="row"
                                    sx={{
                                        alignItems: 'center',
                                        width: '100%',
                                        minWidth: 0,
                                        gap: 1,
                                    }}>
                                    <IconButton
                                        sx={{
                                            '&:hover': { borderColor: '#F4F4F6' },
                                            background: ' #fff',
                                        }}
                                        variant="text"
                                        color="primary"
                                        onClick={() =>
                                            navigate(
                                                requestDetail?.workplace_id
                                                    ? `/project/?workplaceId=${requestDetail?.workplace_id}`
                                                    : `/requests?is_archived=${requestDetail?.is_archived ?? false}`,
                                            )
                                        }>
                                        <ArrowBackIcon />
                                    </IconButton>
                                    <Stack
                                        direction="column"
                                        sx={{
                                            alignItems: 'flex-start',
                                            width: 'calc(100% - 56px)',
                                        }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                width: '100%',
                                                color: '#121317',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                            {requestDetail ? (
                                                requestDetail?.name
                                            ) : (
                                                <Skeleton width={210} />
                                            )}
                                        </Typography>
                                        <Stack
                                            sx={{
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 1,
                                                width: '100%',
                                            }}>
                                            {requestDetail ? (
                                                <>
                                                    <ProjectItem
                                                        projectId={requestDetail.workplace_id}
                                                    />
                                                    {!!requestDetail.scheduled_reply_count &&
                                                        !requestDetail.is_archived &&
                                                        !isTempUser && (
                                                            <RegularRequestsChip
                                                                threadId={threadId}
                                                                requestsCount={
                                                                    requestDetail.scheduled_reply_count
                                                                }
                                                            />
                                                        )}
                                                </>
                                            ) : (
                                                <Skeleton width={210} />
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>

                                {requestDetail && (
                                    <Stack direction="row" alignItems="center">
                                        {!requestDetail.is_archived && (
                                            <Button
                                                sx={{
                                                    minWidth: '24px',
                                                    p: 1,
                                                    '.MuiButton-endIcon': {
                                                        m: 0,
                                                    },
                                                }}
                                                size="small"
                                                onClick={handleClickOpenShareDialog}
                                                endIcon={<ShareIcon />}>
                                                {!isMobile && 'Поделиться'}
                                            </Button>
                                        )}

                                        <RequestItemMenu
                                            request={requestDetail}
                                            threadId={threadId}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                            <Divider />
                        </Stack>
                        {shouldShowMessagesPlaceholder ? (
                            <Stack
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'auto',
                                    flexGrow: 1,
                                    flexBasis: 0,
                                    p: '16px 0',
                                }}>
                                <Stack
                                    sx={{
                                        width: '100%',
                                        gap: 1,
                                        alignItems: 'center',
                                    }}>
                                    <Skeleton
                                        variant="circular"
                                        width="160px"
                                        height={24}
                                        sx={{ margin: '16px', borderRadius: '24px' }}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={104}
                                        sx={{ borderRadius: '24px' }}
                                    />
                                </Stack>
                                <Stack
                                    sx={{
                                        width: '100%',
                                        gap: 1,
                                        alignItems: 'center',
                                    }}>
                                    <Skeleton
                                        variant="circular"
                                        width="160px"
                                        height={24}
                                        sx={{ margin: '16px', borderRadius: '24px' }}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={104}
                                        sx={{ borderRadius: '24px' }}
                                    />
                                </Stack>
                                <Stack
                                    sx={{
                                        width: '100%',
                                        gap: 1,
                                        alignItems: 'center',
                                    }}>
                                    <Skeleton
                                        variant="circular"
                                        width="160px"
                                        height={24}
                                        sx={{ margin: '16px', borderRadius: '24px' }}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={104}
                                        sx={{ borderRadius: '24px' }}
                                    />
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack
                                ref={messagesListRef}
                                onScroll={handleScroll}
                                onScrollCapture={handleStoreScrollPosition}
                                id="infinite-scroll-container"
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'auto',
                                    flexGrow: 1,
                                    flexBasis: 0,
                                    flexDirection: 'column-reverse',

                                    [theme.breakpoints.down('sm')]: {
                                        gap: '24px',
                                    },
                                }}>
                                <InfiniteScroll
                                    scrollableTarget="infinite-scroll-container"
                                    dataLength={requestMessages?.length ?? 0}
                                    next={fetchNextPage}
                                    hasMore={hasNextPage}
                                    loader={<LinearProgress />}
                                    inverse
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column-reverse',
                                        gap: '4px',
                                    }}>
                                    <span ref={messagesEndRef} />
                                    {_.map(requestMessages, (request, index) => (
                                        <Message
                                            key={request.id}
                                            threadId={threadId}
                                            request={request}
                                            isLastMessage={index === 0}
                                            isFirstMessage={
                                                index === (requestMessages?.length ?? 0) - 1
                                            }
                                            createdAtLast={
                                                requestMessages?.at(index + 1)?.created_at
                                            }
                                            onChangeSuggestionText={handleOnChangeSuggestionText}
                                        />
                                    ))}
                                </InfiniteScroll>
                            </Stack>
                        )}

                        {errorRequestDetail && (
                            <Typography color="error" variant="h4" sx={{ margin: '40px auto ' }}>
                                Такого запроса не существует! Выберите другой запрос из списка
                                запросов
                            </Typography>
                        )}

                        <Box
                            sx={{
                                width: '100%',
                                position: 'relative',
                            }}>
                            {showScrollButton && (
                                <ScrollDownButton onClick={scrollToBottom}>
                                    <ArrowUpIcon
                                        style={{
                                            transform: 'rotate(180deg)',
                                        }}
                                    />
                                </ScrollDownButton>
                            )}
                            <WrapperRequestInput
                                suggestedRequest={suggestionText}
                                onMounted={() => setIsInputMounted(true)}
                                onSendMessage={scrollToBottom}
                            />
                        </Box>

                        {isModelOrAssistantsUnavailable === true && (
                            <Grid
                                container
                                sx={{
                                    width: '100%',
                                    background: '#fff',
                                }}>
                                <Typography
                                    color="error"
                                    variant="h4"
                                    sx={{ margin: '40px auto ' }}>
                                    {` Этот запрос не может быть продолжен из-за того, что выбранная модель/ассистент
                                 не доступен на Текущем уровене`}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </BlockWrapper>
            <TextSelectionMenu />
            {requestDetail && openShareDialog && (
                <ShareDialog
                    open={openShareDialog}
                    onClose={handleClickCloseShareDialog}
                    entityId={requestDetail?.id}
                    entityName="thread"
                    onCopy={handleCopy}
                />
            )}
        </>
    )
}
